import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  Migration as SchemaMigration,
  MigrationMinimal,
  MigrationAdd,
  MigrationUpdate,
  MigrationDeploymentAdd,
  MigrationDelete,
  ScheduledTask
} from '@schema';
import api from '@/common/utils/api';
import useCommonStore, { type FetchOptions } from '@/common/store/pinia/common';
import mapper, { MappedMigration } from '@/common/store/pinia/migrations/migrationsMapper';
import { getCloudInstanceFetchUrl, getDeploymentsUrl, getMigrationsUrl } from '@/common/store/pinia/maasUrls';
import i18n from '@/common/i18n';
import { NotificationType } from '@/common/constants/common.constants';
import { takeLastByKeyIgnoreCancel } from '@/common/utils/takeLastByKeyIgnoreCancel';
import { useNotifications } from '@/common/utils/notifications/notificationsPlugin';
import { isNotificationForCurrentProject } from '@/common/utils/notifications/isNotificationForCurrentProject';
import useAuthStore from '@/common/store/pinia/auth';
import waitTask from '@/common/utils/waitTask';
import CancellationError from '@/common/utils/errors/CancellationError';
import { notifyDiskCleanupStatusChange, DiskCleanupStatus } from '@/common/store/pinia/migrations/disksCleanupNotifications';

export interface CreateDeploymentOptions {
  vmName: string;
  cloneDisks?: boolean;
  forceDeployment?: boolean;
  osMorphing?: boolean;
  saveSnapshot?: boolean;
  start?: boolean;
  windowsUserScript?: string;
  linuxUserScript?: string;
}

export type MigrationAsideNavigationItem = {
  id: string;
  name: string;
};

const MIGRATION_CREATING_OPERATION_ID = 'migration-creating';
const HANDLED_COMPUTE_TYPES = new Set(['migrations', 'migration_replications', 'migration_deployments', 'migration_disks_cleanup']);

interface PendingCreation {
  total: number;
  processed: number;
  failed: number;
}

function toMigrationDeploymentAdd(options: CreateDeploymentOptions): MigrationDeploymentAdd {
  return {
    name: options.vmName,
    clone_disks: options.cloneDisks ?? false,
    force_deployment: options.forceDeployment ?? false,
    os_morphing: options.osMorphing ?? false,
    save_snapshot: options.saveSnapshot ?? false,
    start: options.start ?? false,
    ...(options.windowsUserScript ? { windows_user_script: options.windowsUserScript } : {}),
    ...(options.linuxUserScript ? { linux_user_script: options.linuxUserScript } : {})
  };
}

export const useMigrationsStore = defineStore('migrations', () => {
  const { notify } = useNotifications();

  // Migrations deleted from the UI with `delete_disks`: the backend keeps the transfer alive until
  // its disk-cleanup execution finalizes and only then emits `migrations/deleted`. The UI must
  // neither notify about that cleanup nor let a refetch bring the deleted row back. Ids stay in the
  // set for the session because the cleanup and deleted events are not guaranteed to arrive in order.
  const pendingDeletionIds = new Set<string>();

  const commonStore = useCommonStore<SchemaMigration, MappedMigration>({
    getUrl: getMigrationsUrl,
    itemKey: 'id',
    plural: (data) => (data as { data: SchemaMigration[] }).data
      .filter(migration => !migration.id || !pendingDeletionIds.has(migration.id)),
    mapItem: mapper,
    updateMethod: 'patch',
    dataChangeMode: 'direct'
  })();

  const {
    getItems,
    remove,
    fetchItem,
    fetchItemsWithFiltersThrottled
  } = commonStore;

  // Overrides commonStore.fetchLastItem so superseded fetches don't surface as unhandled rejections.
  // Skips migrations pending deletion: the transfer still exists on the backend, so a refetch
  // triggered by a late socket event would re-insert the row the user has already deleted.
  const fetchLastItem = (id: ID, options: FetchOptions = {}): Promise<void> => {
    if (pendingDeletionIds.has(String(id))) return Promise.resolve();
    return takeLastByKeyIgnoreCancel(
      abortController => commonStore.fetchItem(id, { ...options, abortController }),
      `${getMigrationsUrl()}/${id}`
    );
  };

  const pendingCreation = ref<PendingCreation | null>(null);

  // Each notify call produces a separate notification (no shared id), so the success/error result
  // appears alongside the auto-dismissing INFO toast instead of being deduped against it.
  const showMigrationCreatingNotification = () => notify({
    type: NotificationType.INFO,
    message: i18n.t('Creating migration...'),
    showBottom: true,
    operationId: MIGRATION_CREATING_OPERATION_ID
  });

  const showMigrationCreationResultNotification = (total: number, failed: number, errorMessage?: string) => {
    if (failed === 0) {
      notify.success({
        title: i18n.t('Migration created'),
        message: i18n.tc(
          'Migration successfully created|{num} migrations successfully created',
          total,
          { num: total }
        ),
        showBottom: true,
        operationId: MIGRATION_CREATING_OPERATION_ID
      });
      return;
    }

    notify.error({
      title: i18n.t('Operation failed'),
      message: errorMessage || i18n.t('Operation failed'),
      showBottom: true,
      operationId: MIGRATION_CREATING_OPERATION_ID
    });
  };

  const failPendingCreation = (errorMessage?: string) => {
    if (!pendingCreation.value) return;
    const { total } = pendingCreation.value;
    pendingCreation.value = null;
    showMigrationCreationResultNotification(total, total, errorMessage);
  };

  const watchCreationTask = (taskId: string | undefined, owned: PendingCreation) => {
    if (!taskId) return;
    waitTask(taskId).catch((err: unknown) => {
      if (pendingCreation.value !== owned) return;
      if (err instanceof CancellationError) {
        pendingCreation.value = null;
        return;
      }
      failPendingCreation(err instanceof Error ? err.message : String(err));
    });
  };

  const fetchInstanceFromCloud = (migrationId: string, cloudId: string, instanceId: string) => api.post(
    getCloudInstanceFetchUrl(cloudId, instanceId),
    { migration_id: migrationId }
  );

  const migrations = computed(() => getItems.value);

  const asideNavigationItems = ref<MigrationAsideNavigationItem[]>([]);

  const fetchAsideNavigation = async () => {
    const response = await api.get<{ data: MigrationMinimal[] }>(getMigrationsUrl());
    asideNavigationItems.value = response?.data?.data.map((row) => ({
      id: row.id ?? '',
      name: row.name ?? ''
    }));
  };

  const clearAsideNavigation = () => {
    asideNavigationItems.value = [];
  };

  const deleteMigration = (id: string, payload?: MigrationDelete) => {
    if (!payload?.delete_disks) return remove(id, payload);

    pendingDeletionIds.add(id);
    return remove(id, payload).catch((error: unknown) => {
      pendingDeletionIds.delete(id);
      throw error;
    });
  };

  const deleteItems = (ids: string[], payload?: MigrationDelete) => Promise.all(ids.map(id => deleteMigration(id, payload)));

  const cleanupDisks = (id: string) => api.post(`${getMigrationsUrl()}/${id}/cleanup_disks`)
      .then(() => fetchItem(id));

  const createDeployment = (migrationId: string, options: CreateDeploymentOptions) => {
    const body = toMigrationDeploymentAdd(options);
    return api.post(getDeploymentsUrl(migrationId), body)
      .then(() => fetchItem(migrationId));
  };

  const addMigration = (body: MigrationAdd) => {
    let owned: PendingCreation | null = null;
    if (!pendingCreation.value) {
      const projectId = useAuthStore().currentUserProjectId;
      if (isNotificationForCurrentProject(projectId)) {
        const total = Array.isArray(body.instances) ? body.instances.length : 1;
        owned = { total, processed: 0, failed: 0 };
        pendingCreation.value = owned;
        showMigrationCreatingNotification();
      }
    }
    return api.post<ScheduledTask>(getMigrationsUrl(), body)
      .then((response) => {
        if (owned) watchCreationTask(response?.data?.task_id, owned);
        return response;
      })
      .catch((err) => {
        if (owned) failPendingCreation();
        throw err;
      });
  };

  const updateMigration = (id: string, body: MigrationUpdate) => api.patch(`${getMigrationsUrl()}/${id}`, body)
      .then(() => fetchItem(id));

  const onCompute = (data: SocketEvent<{
    id?: string;
    migration_id?: string;
    project_id?: string;
    status?: string;
  }>) => {
    const obj = data.object;
    if (!obj) return;
    if (!HANDLED_COMPUTE_TYPES.has(data.type)) return;
    if (!isNotificationForCurrentProject(obj.project_id)) return;

    if (data.type === 'migrations') {
      const { id } = obj;
      if (!id) return;
      if (data.operation === 'deleted') {
        fetchItemsWithFiltersThrottled.clearId(id);
        commonStore.removeItem(id);
      } else {
        fetchLastItem(id);
      }

      if (pendingCreation.value
        && (data.operation === 'created' || data.operation === 'failed')) {
        const pending = pendingCreation.value;
        pending.processed++;
        if (data.operation === 'failed') pending.failed++;

        if (pending.processed >= pending.total) {
          const { total, failed } = pending;
          pendingCreation.value = null;
          showMigrationCreationResultNotification(total, failed);
        }
      }
      return;
    }

    if (data.type === 'migration_disks_cleanup') {
      const migrationId = obj.migration_id;
      if (!migrationId || pendingDeletionIds.has(migrationId)) return;
      const migrationName = commonStore.getItemById.value(migrationId)?.name ?? '';
      notifyDiskCleanupStatusChange(obj.status as DiskCleanupStatus, migrationId, migrationName);
      return;
    }

    const migrationId = obj.migration_id;
    if (migrationId) fetchLastItem(migrationId);
  };

  return {
    ...commonStore,
    fetchLastItem,
    migrations,
    asideNavigationItems,
    fetchAsideNavigation,
    clearAsideNavigation,
    deleteMigration,
    deleteItems,
    cleanupDisks,
    createDeployment,
    fetchInstanceFromCloud,
    addMigration,
    updateMigration,
    onCompute
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useMigrationsStore;
